import dayjs from 'dayjs';
import { makeAutoObservable, runInAction } from 'mobx';
import { MouseEvent } from 'react';
import { HolidayType } from '../../components/Holidays/graphql';
import {
  CardAutoFitOptions,
  CardDurationModalType,
  CardResizeRange,
  CardRoomsModalType,
  ConflictModalType,
  GridContext,
  GridHighlightType,
  PickedCard,
  PlacedTeachingBlockCardType,
  ResizeEdge,
  RowItem,
  TeachingBlockCardType,
  TimeConflictModalType,
} from '../../components/TeachingBlockGrid/types';
import type { BoardLayout } from '../TimetableVersion/PinboardStoreTypes';
import { persistSettings } from '../userConfig/persistSettings';
import type { UserConfigStore } from '../userConfig/UserConfigStore';
import type { TeachingBlockStore } from './TeachingBlockStore';

/**
 * UI-State der TeachingBlock-Planung (Phase 7.3, an `PinboardUIStore` orientiert).
 *
 * Hält den achsen-unabhängigen UI-State (Pick/Pin/Select, Highlight, Modals) sowie
 * die TeachingBlock-spezifische Pick/Place/Modal-Orchestrierung. Die Kartendaten,
 * Platzierung und das Ferien-Splitting liegen weiterhin im `cardStore`; der UIStore
 * ruft diese über `this.root.*` (Fassaden-Delegationen) auf, damit die öffentliche
 * Store-API für die Grid-Komponenten unverändert bleibt.
 */
/**
 * Felder des Epochen-Boards, die pro Person gespeichert werden. Wie beim
 * Stundenplan-Board ohne Transientes, ohne `hiddenRows`, ohne `isFullscreen`
 * und ohne `gridContext` (der laeuft ueber `selectContext`).
 */
export const TEACHING_BLOCK_SETTING_KEYS = [
  'rowHeight',
  'groupRoomsByBuilding',
  'showTeacherInsteadOfSubject',
  'cardColorBySubject',
  'boardLayout',
  'boardTopHeight',
  'boardSplitWidth',
  'boardPickerWidth',
  'boardPickerHeight',
  'showPreviewMap',
  'previewMapContext',
  'previewMapAuto',
  'boardPreviewMapWidth',
  'boardPreviewMapHeight',
  'highlightMode',
] as const;

export class TeachingBlockUIStore {
  public gridContext: GridContext = 'classes';

  public selectedCard: TeachingBlockCardType | null = null;
  public pickedCard: PickedCard | null = null;
  public pinnedCard: TeachingBlockCardType | null = null;

  public selectedRows: RowItem[] = [];
  private _lastSelectedRow: RowItem | null = null;

  /**
   * Raum-Kontext: der Raum (row.value), aus dessen Zeile die aktuell gepickte Karte
   * aufgenommen wurde. Beim Zurücklegen in den Picker wird genau dieser Raum entfernt
   * (Pendant zu `_oldRoomUuidToReplace` im PinboardUIStore). `null`, wenn die Karte aus
   * dem Picker (raumlos) kommt oder außerhalb des Raum-Kontexts gepickt wurde.
   */
  private _pickedFromRoom: string | null = null;

  /**
   * Raum-Kontext, Kopiermodus (Shift beim Pick): die Karte wird beim Ablegen in einen Raum
   * NICHT verschoben, sondern der Raum wird hinzugefügt und die Karte bleibt in der Hand –
   * so lässt sich dieselbe Karte in mehrere Räume setzen. Zurücklegen ins Panel behält alle.
   */
  private _pickIsCopy: boolean = false;

  public get pickedFromRoom(): string | null {
    return this._pickedFromRoom;
  }
  public get pickIsCopy(): boolean {
    return this._pickIsCopy;
  }

  /**
   * Zeilen der aktuell gepickten Karte (Perf, Fix C – Pinboard-Pendant). Nur
   * `GridWeek`s dieser Zeilen abonnieren beim Picken `pickedCard` und rendern neu;
   * alle anderen Zeilen bleiben unangetastet. Wird in `pickCard` gesetzt und bei
   * `onEscape`/`setContext`/`pickCard(null)` geleert.
   *
   * Perf: bewusst eine `Map` (MobX `ObservableMap.has` ist **pro Schlüssel** reaktiv) –
   * eine `Set` hat nur EIN Atom, dann würde jede Änderung ALLE `GridWeek`s neu rendern.
   * Außerdem NIE neu zuweisen, sondern in-place mutieren (sonst feuert das Feld-Atom).
   */
  public rowsToHighlight = new Map<string, true>();

  public conflictModal: ConflictModalType = {};
  public timeConflictModal: TimeConflictModalType = {};
  public cardDurationModal: CardDurationModalType = {};
  public cardRoomsModal: CardRoomsModalType = {};

  public highlightMode: GridHighlightType[] = ['warnings'];
  public resizingCard: boolean = false;

  /** Pinboard-Parität: ein-/ausgeblendete Zeilen (Key = `row.value`). */
  public hiddenRows: Set<string> = new Set();
  /** Pinboard-Parität: Zeilenhöhe in px (Default = CSS `--pinboard-cell-size-small`). */
  public rowHeight: number = 32;
  /** Pinboard-Parität: Vollbild-Status (Quelle der Wahrheit für GridMenuBar). */
  public isFullscreen: boolean = false;
  /** Pinboard-Parität: Kartensuche (filtert Stapel + dimmt platzierte Karten). */
  public cardSearchString: string = '';
  /** Gruppiert die Raum-Zeilen im Raum-Kontext nach Gebäuden (nur wenn Gebäude gepflegt sind). */
  public groupRoomsByBuilding: boolean = true;
  /** Zeigt auf den Kärtchen das LuL-Kürzel statt des Fach-Kürzels. */
  public showTeacherInsteadOfSubject: boolean = false;
  /** Färbt die Kärtchen in der Fachfarbe statt im Lehrkraft-Farbverlauf. */
  public cardColorBySubject: boolean = false;

  /**
   * Anordnung von Auswahl (CardPicker + CardInfo) und Board. Siehe {@link BoardLayout}.
   * `boardTopHeight` (px) gilt im `stacked`-Layout, `boardSplitWidth` (%) im `split`-Layout —
   * beide per verschiebbarer Trennlinie im Board justierbar.
   */
  public boardLayout: BoardLayout = 'stacked';
  // Default an die Design-Höhe der CardInfo (`--card-info-height` = 268px) angelehnt, damit im
  // `stacked`-Layout ohne Nachjustieren nichts vom Auswahl-Bereich abgeschnitten wird.
  public boardTopHeight: number = 280;
  public boardSplitWidth: number = 34;
  /** Anteil des CardPickers am Auswahl-Bereich (Rest = CardInfo), je Layout eigene Achse. */
  public boardPickerWidth: number = 70;
  public boardPickerHeight: number = 60;

  /**
   * Optionale Vorschau-Karte (Mini-Map des Plans) zwischen CardPicker und CardInfo. Standard aus;
   * eingeblendet per Trennlinie justierbar und unabhängig vom Board-Kontext (Klassen/Lehrkräfte/
   * Räume) einstellbar, um sich besser zu orientieren.
   */
  public showPreviewMap: boolean = false;
  public previewMapContext: GridContext = 'classes';
  /**
   * Automatik: Mit einer Karte in der Hand zeigt die Vorschau statt eines Kontexts mehrere
   * Abschnitte untereinander – genau die Ressourcen, die man im aktuellen Board-Kontext beim
   * Stecken NICHT sieht. Standard an, weil das der Regelfall beim Stecken ist; eine manuelle
   * Kontext-Wahl schaltet sie ab (siehe `setPreviewMapContext`).
   */
  public previewMapAuto: boolean = true;
  public boardPreviewMapWidth: number = 30;
  public boardPreviewMapHeight: number = 30;

  private disposeSettings: (() => void) | null = null;
  private userConfig: UserConfigStore | null = null;

  constructor(
    private root: TeachingBlockStore,
    userConfig: UserConfigStore | null = null,
  ) {
    makeAutoObservable(this);

    this.userConfig = userConfig;
    if (userConfig) {
      this.disposeSettings = persistSettings(this, userConfig, 'teachingBlock', TEACHING_BLOCK_SETTING_KEYS);
    }
  }

  /** Beendet das Beobachten der persistierten Felder. */
  public dispose() {
    this.disposeSettings?.();
    this.disposeSettings = null;
  }

  // ─── Kontext / Selektion ──────────────────────────────────────────────────

  public setGroupRoomsByBuilding(value: boolean) {
    runInAction(() => {
      this.groupRoomsByBuilding = value;
    });
  }

  public setShowTeacherInsteadOfSubject(value: boolean) {
    runInAction(() => {
      this.showTeacherInsteadOfSubject = value;
    });
  }

  public setCardColorBySubject(value: boolean) {
    runInAction(() => {
      this.cardColorBySubject = value;
    });
  }

  public setContext(type: GridContext) {
    runInAction(() => {
      this.gridContext = type;
      this.rowsToHighlight.clear();
    });
  }

  /**
   * Bewusster Kontextwechsel durch die Nutzerin oder den Nutzer — nur dieser wird
   * gespeichert. `setContext` bleibt ohne Persistenz, weil `_loadCards` es beim
   * Laden selbst aufruft.
   */
  public selectContext(type: GridContext) {
    this.setContext(type);
    this.userConfig?.updateSettings('teachingBlock', { gridContext: type });
  }

  /**
   * Stellt den zuletzt gewaehlten Kontext her. Erst **nach** `_loadCards`
   * aufrufen, das den Kontext selbst setzt.
   */
  public restorePersistedContext() {
    // `context` hat im Schema bewusst keinen Default: Ohne bewusste Auswahl ist der
    // Wert `undefined`, und der von der Kartenlogik gesetzte Kontext bleibt stehen.
    const stored = this.userConfig?.getSettings('teachingBlock').gridContext;
    if (stored && stored !== this.gridContext) {
      this.setContext(stored);
    }
  }

  public selectCard(card: TeachingBlockCardType | null) {
    if (this.pinnedCard || this.pickedCard) return;
    runInAction(() => {
      this.selectedCard = card;
    });
  }

  public onEscape(card: PlacedTeachingBlockCardType | TeachingBlockCardType) {
    if (card.startDate && card.endDate) {
      this.root.getAvailabilityStore()?.setCardsResourcesPlaced([this.root.cardStore.cardToAvailabilityCard(card)], {
        start: card.startDate,
        end: card.endDate,
      });
    }

    runInAction(() => {
      this.pickedCard = null;
      this.rowsToHighlight.clear();
      // Raum-Kontext: es wurde beim Pick nichts entfernt → Abbruch lässt den Zustand unverändert.
      this._pickedFromRoom = null;
      this._pickIsCopy = false;
    });
  }

  public pickCard(
    card: TeachingBlockCardType | PlacedTeachingBlockCardType | null,
    position?: { x: number; y: number },
    fromStack?: boolean,
    fromRoomUuid?: string,
    isCopy?: boolean,
  ) {
    if (card?.locked) return;

    const res = this.root.freeCards.filter(
      (c) => c.lesson?.uuid === card?.lesson?.uuid && c.duration === card?.duration,
    ).length;

    // prevent picking if version is active and start of card is in past
    if (
      this.root.readonly ||
      (!!this.root.currentVersion.active &&
        !!card?.startDate &&
        dayjs(card.startDate).startOf('day').isSameOrBefore(dayjs().startOf('day'), 'day'))
    )
      return;
    if (card?.startDate && card?.endDate) {
      this.root.getAvailabilityStore()?.removeCardsResourcesPlaced([this.root.cardStore.cardToAvailabilityCard(card)], {
        start: card.startDate,
        end: card.endDate,
      });
    }

    runInAction(() => {
      if (card !== null) {
        this.pickedCard = {
          card: card,
          counter: fromStack ? res : 1,
          position: position ?? { x: 0, y: 0 },
          fromStack: fromStack ?? false,
        };
        // In-place statt Neuzuweisung: nur die tatsächlich betroffenen Zeilen (alte +
        // neue Highlight-Zeilen) rendern neu, nicht das ganze Grid.
        this.rowsToHighlight.clear();
        if (this.gridContext === 'rooms') {
          // Raum-Kontext: die Karte ist zeitlich schon platziert, gesucht wird nur der Raum
          // (die Zeile). Deshalb ALLE Raum-Zeilen hervorheben, damit die Drop-Zone (die
          // eigenen Zeit-Spalten der Karte) in jeder Raum-Zeile markiert werden kann.
          this.root.cardStore.getRows().forEach((r) => this.rowsToHighlight.set(r.value, true));
          this._pickIsCopy = isCopy ?? false;
          // Kopiermodus fügt Räume additiv hinzu → kein „Quell-Raum", der ersetzt/entfernt wird.
          this._pickedFromRoom = this._pickIsCopy ? null : (fromRoomUuid ?? null);
        } else {
          this.root.cardStore.getRowValuesForCard(card).forEach((value) => this.rowsToHighlight.set(value, true));
          this._pickedFromRoom = null;
          this._pickIsCopy = false;
        }
      } else {
        this.pickedCard = null;
        this.rowsToHighlight.clear();
        this._pickedFromRoom = null;
        this._pickIsCopy = false;
      }
      this.selectedCard = card;
    });
  }

  public pinCard(card: TeachingBlockCardType) {
    runInAction(() => {
      if (this.pinnedCard) {
        if (this.pinnedCard.uuid === card.uuid) {
          this.unpinCard();
        } else {
          this.unpinCard();
          this.pinnedCard = card;
          card.pinned = true;
        }
      } else {
        this.pinnedCard = card;
        card.pinned = true;
      }
      this.selectCard(card);
    });
  }

  public unpinCard() {
    runInAction(() => {
      if (!this.pinnedCard) return;
      this.pinnedCard.pinned = false;
      this.pinnedCard = null;
    });
  }

  public selectRows(rows: RowItem[]) {
    runInAction(() => {
      this.selectedRows = rows;
      this.reset();
    });
  }

  public rowClickHandler(e: MouseEvent<HTMLDivElement>, row: RowItem) {
    if (this._lastSelectedRow?.value === row?.value) {
      this.selectRows(this.selectedRows.filter((r) => r.value !== row.value));
      this._lastSelectedRow = null;
      return;
    } else if (e.altKey || e.ctrlKey) {
      this.selectRows([...this.selectedRows, row]);
    } else if (e.shiftKey) {
      const prevIndex = this._lastSelectedRow
        ? this.root.rows.findIndex((rowItem) => rowItem.value === this._lastSelectedRow?.value)
        : 0;
      const clickIndex = this.root.rows.findIndex((rowItem) => rowItem.value === row.value);
      const selected =
        clickIndex > prevIndex
          ? this.root.rows.slice(prevIndex, clickIndex + 1)
          : this.root.rows.slice(clickIndex, prevIndex + 1);
      this.selectRows([
        ...this.selectedRows,
        ...selected.filter(
          (item) => this.selectedRows.findIndex((selectedRow) => selectedRow.value === item.value) === -1,
        ),
      ]);
    } else if (this.selectedRows.length === 1 && this.selectedRows[0] === row) {
      this.selectRows([]);
    } else {
      this.selectRows([row]);
    }
    this._lastSelectedRow = row;
  }

  // ─── Bulk-Aktionen pro Row ────────────────────────────────────────────────

  public lockAllCards() {
    runInAction(() => {
      this.root.allPlacedCards.forEach((card) => {
        this.root.cardStore.lockCard(card);
      });
    });
  }

  public unlockAllCards() {
    runInAction(() => {
      this.root.allPlacedCards.forEach((card) => {
        this.root.cardStore.unlockCard(card);
      });
    });
  }

  public discardAllCards() {
    runInAction(() => {
      this.root.allPlacedCards.forEach((card) => this.root.cardStore.discardCard(card));
    });
  }

  public lockCardsInSelectedRows() {
    runInAction(() => {
      this.selectedRows.forEach((row) => this.lockCardsInRow(row));
    });
  }

  public lockCardsInRow(row: RowItem) {
    const cards = this.root.allPlacedCards.filter((card) => {
      return this.root.cardStore.isUuidPresentInTimetableCard(card, row.value);
    });
    cards.forEach((card) => this.root.cardStore.lockCard(card));
  }

  public unlockCardsInSelectedRows() {
    runInAction(() => {
      this.selectedRows.forEach((row) => this.unlockCardsInRow(row));
    });
  }

  public unlockCardsInRow(row: RowItem) {
    const cards = this.root.allPlacedCards.filter((card) => {
      return this.root.cardStore.isUuidPresentInTimetableCard(card, row.value);
    });
    cards.forEach((card) => this.root.cardStore.unlockCard(card));
  }

  public discardCardsInSelectedRows() {
    runInAction(() => {
      this.selectedRows.forEach((row) => this.discardCardsInRow(row));
    });
  }

  public discardCardsInRow(row: RowItem) {
    const cards = this.root.allPlacedCards.filter((card) => {
      return this.root.cardStore.isUuidPresentInTimetableCard(card, row.value);
    });
    cards.forEach((card) => this.root.cardStore.discardCard(card));
  }

  // ─── Raum-Kontext: Drop weist der Karte den Raum (Zeile) zu ────────────────

  /** Aktueller Stand der Karten-Räume (nach evtl. vorheriger Zuweisung), als {uuid,name}. */
  private _currentRooms(cardUuid: string): Array<{ uuid: string; name: string }> {
    const current = this.root.cardStore.getParentCard(cardUuid);
    return (current?.rooms ?? []).map((r) => ({ uuid: r.uuid, name: '' }));
  }

  private _clearPick() {
    this.pickedCard = null;
    this.rowsToHighlight.clear();
    this._pickedFromRoom = null;
    this._pickIsCopy = false;
  }

  /**
   * Im Raum-Kontext ist die Zeile ein Raum. Die aufgenommene (zeitlich bereits platzierte)
   * Karte wird beim Drop auf eine Raum-Zeile diesem Raum zugewiesen; Start/Ende bleiben.
   *
   * - Verschiebemodus (Standard-Pick): der aufgenommene Quell-Raum wird durch den Ziel-Raum
   *   ersetzt, weitere Räume der Karte bleiben. Danach ist die Karte abgelegt.
   * - Kopiermodus (Shift-Pick): der Ziel-Raum wird zusätzlich hinzugefügt und die Karte bleibt
   *   in der Hand, sodass sie in weitere Räume gesetzt werden kann.
   */
  public assignRoomToPickedCard(row: RowItem) {
    const picked = this.pickedCard?.card;
    if (!picked) return;
    const card = this.root.cardStore.getParentCard(picked.uuid) ?? picked;

    if (this._pickIsCopy) {
      const current = this._currentRooms(card.uuid);
      if (!current.some((r) => r.uuid === row.value)) {
        void this.root.setRoomsForCards([card], [{ uuid: row.value, name: row.label }], true);
      }
      // Karte bleibt in der Hand (weitere Räume möglich).
      return;
    }

    // Verschieben: Quell-Raum raus, Ziel-Raum rein, übrige behalten.
    const withoutSource = card.rooms.filter((r) => r.uuid !== this._pickedFromRoom);
    const newRooms = withoutSource.some((r) => r.uuid === row.value)
      ? withoutSource.map((r) => ({ uuid: r.uuid, name: '' }))
      : [...withoutSource.map((r) => ({ uuid: r.uuid, name: '' })), { uuid: row.value, name: row.label }];
    void this.root.setRoomsForCards([card], newRooms, false);
    runInAction(() => this._clearPick());
  }

  /**
   * Raum-Kontext: Karte zurück ins Panel legen. Statt die Zeit-Platzierung zu verwerfen, wird
   * im Verschiebemodus nur der aufgenommene Quell-Raum entfernt (weitere Räume bleiben; die
   * Karte erscheint erst im Picker, wenn kein Raum mehr übrig ist). Im Kopiermodus bleiben alle
   * bis dahin abgelegten Räume erhalten – es wird nur der Pick beendet.
   */
  public removePickedRoomToStack() {
    const picked = this.pickedCard?.card;
    if (!picked) return;
    const card = this.root.cardStore.getParentCard(picked.uuid) ?? picked;

    if (!this._pickIsCopy && this._pickedFromRoom) {
      const newRooms = card.rooms
        .filter((r) => r.uuid !== this._pickedFromRoom)
        .map((r) => ({ uuid: r.uuid, name: '' }));
      void this.root.setRoomsForCards([card], newRooms, false);
    }
    runInAction(() => this._clearPick());
  }

  // ─── Konflikt-/Ferien-Auflösung ───────────────────────────────────────────

  public ignoreConflict() {
    const card = this.conflictModal.card;
    const startDateString = this.conflictModal.startDateString;
    if (!card || !startDateString) throw new Error('conflict data is undefined');
    this.root.placeCard(card, startDateString, { x: 0, y: 0 }, true);
    this._resetConflict();
  }

  public abortConflict() {
    this._resetConflict();
  }

  public ignoreTimeConflict() {
    const card = this.timeConflictModal.card;
    const startDateString = this.timeConflictModal.startDateString;
    const endDateString = this.timeConflictModal.endDateString;
    if (!card || !startDateString) throw new Error('timeConflict data is undefined');
    if (this.timeConflictModal.holidayUuids?.includes('school-year-end-indicator')) {
      this.resetTimeConflict();
      return;
    }
    if (endDateString) {
      // Resize-Fall: Karte überspannt die Ferien als ein Block (includeHoliday=true)
      // über den gezogenen Bereich.
      void this.root.updateCard(card, startDateString, endDateString, true, false);
      this.resetTimeConflict();
      return;
    }
    this.root.cardStore._placeCard(
      {
        ...card,
        includeHoliday: true,
      },
      startDateString,
    );
    this.resetTimeConflict();
  }

  public abortTimeConflict() {
    this.resetTimeConflict();
  }

  private _resetConflict() {
    runInAction(() => {
      this.setConflictModal({
        isOpen: false,
      });
    });
  }

  public resetTimeConflict() {
    runInAction(() => {
      this.setTimeConflictModal({
        card: undefined,
        week: undefined,
        isOpen: false,
        isLoading: false,
      });
    });
  }

  public reset() {
    this.pickCard(null);
    this.unpinCard();
    this.selectedCard = null;
  }

  private _canAdjustCard(card: TeachingBlockCardType | PlacedTeachingBlockCardType) {
    if (!card.startDate || !card.endDate || card.locked || this.root.readonly) return false;

    const today = dayjs().startOf('day');
    const cardStart = dayjs(card.startDate).startOf('day');
    if (this.root.currentVersion.active && cardStart.isSameOrBefore(today, 'day')) {
      return false;
    }

    return true;
  }

  /**
   * Günstige O(1)-Prüfung, ob eine Karte überhaupt resizebar ist (nicht gesperrt,
   * nicht readonly, nicht in der Vergangenheit einer aktiven Version). Für die
   * Sichtbarkeit der Resize-Handles – die teuren {@link getCardResizeBounds} werden
   * erst beim tatsächlichen Resize-Start (mousedown) berechnet.
   */
  public canAdjustCard(card: TeachingBlockCardType | PlacedTeachingBlockCardType) {
    return this._canAdjustCard(card);
  }

  private _getNormalizedRawHolidays(): HolidayType[] {
    return [...this.root.rawHolidays].sort((a, b) => dayjs(a.start).startOf('day').diff(dayjs(b.start).startOf('day')));
  }

  /** Letzte Ferien, die komplett vor `reference` enden (oder `null`). */
  private _getHolidayBeforeReference(reference: dayjs.Dayjs): HolidayType | null {
    return (
      [...this._getNormalizedRawHolidays()]
        .reverse()
        .find((holiday) => dayjs(holiday.end).startOf('day').isBefore(reference, 'day')) ?? null
    );
  }

  /** Erste Ferien, die komplett nach `reference` beginnen (oder `null`). */
  private _getHolidayAfterReference(reference: dayjs.Dayjs): HolidayType | null {
    return (
      this._getNormalizedRawHolidays().find((holiday) =>
        dayjs(holiday.start).startOf('day').isAfter(reference, 'day'),
      ) ?? null
    );
  }

  public getCardResizeBounds(
    card: TeachingBlockCardType | PlacedTeachingBlockCardType,
    edge: ResizeEdge,
    range?: CardResizeRange,
    crossHolidays = false,
  ) {
    if (!this._canAdjustCard(card)) return null;

    const today = dayjs().startOf('day');
    const visibleStart = dayjs(range?.startDateString ?? card.startDate).startOf('day');
    const visibleEnd = dayjs(range?.endDateString ?? card.endDate).startOf('day');
    const schoolYearStart = dayjs(this.root.schoolYear.start).startOf('day');
    const schoolYearEnd = dayjs(this.root.schoolYear.end).startOf('day');
    const todayInTimeframe = !today.isBefore(schoolYearStart, 'day') && !today.isAfter(schoolYearEnd, 'day');

    let minDate = schoolYearStart;
    let maxDate = schoolYearEnd;

    if (edge === 'start') {
      maxDate = visibleEnd.subtract(1, 'day');

      const previousCard = this.root.cardStore.getPreviousCard(card);
      if (previousCard?.endDate) {
        minDate = dayjs(previousCard.endDate).startOf('day').add(1, 'day');
      }

      // `crossHolidays`: beim Resize darf über/in Ferien gezogen werden (das Modal
      // entscheidet danach split/ignore). Sonst klemmt der Start an der Ferien-Grenze.
      const previousHoliday = crossHolidays ? null : this.root.cardStore.getPreviousHoliday(card);
      if (previousHoliday?.end) {
        const holidayMinDate = dayjs(previousHoliday.end).startOf('day').add(1, 'day');
        if (holidayMinDate.isAfter(minDate, 'day')) {
          minDate = holidayMinDate;
        }
      }

      if (this.root.currentVersion.active && todayInTimeframe) {
        const todayMinDate = today.add(1, 'day');
        if (todayMinDate.isAfter(minDate, 'day')) {
          minDate = todayMinDate;
        }
      }
    } else {
      minDate = visibleStart.add(1, 'day');

      const nextCard = this.root.cardStore.getNextCard(card);
      if (nextCard?.startDate) {
        maxDate = dayjs(nextCard.startDate).startOf('day').subtract(1, 'day');
      }

      const nextHoliday = crossHolidays ? null : this.root.cardStore.getNextHoliday(card);
      if (nextHoliday?.start) {
        const holidayMaxDate = dayjs(nextHoliday.start).startOf('day').subtract(1, 'day');
        if (holidayMaxDate.isBefore(maxDate, 'day')) {
          maxDate = holidayMaxDate;
        }
      }

      if (this.root.currentVersion.active && todayInTimeframe) {
        const todayMinDate = today.add(1, 'day');
        if (todayMinDate.isAfter(minDate, 'day')) {
          minDate = todayMinDate;
        }
      }
    }

    if (maxDate.isBefore(minDate, 'day')) return null;

    return {
      minDateString: minDate.format('YYYY-MM-DD'),
      maxDateString: maxDate.format('YYYY-MM-DD'),
    };
  }

  public async resizeCardEdge(
    card: TeachingBlockCardType | PlacedTeachingBlockCardType,
    edge: ResizeEdge,
    targetDateString: string,
    range?: CardResizeRange,
    isVirtual: boolean = false,
  ) {
    if (!card.startDate || !card.endDate) return false;

    // crossHolidays: über/in Ferien resizen erlaubt – die Entscheidung trifft danach
    // das Time-Conflict-Modal (aufteilen/ignorieren).
    const bounds = this.getCardResizeBounds(card, edge, range, true);
    if (!bounds) return false;

    const minDate = dayjs(bounds.minDateString).startOf('day');
    const maxDate = dayjs(bounds.maxDateString).startOf('day');
    const targetDate = dayjs(targetDateString).startOf('day');
    const clampedDate = targetDate.isBefore(minDate, 'day')
      ? minDate
      : targetDate.isAfter(maxDate, 'day')
        ? maxDate
        : targetDate;

    const currentStartDateString = dayjs(card.startDate).startOf('day').format('YYYY-MM-DD');
    const currentEndDateString = dayjs(card.endDate).startOf('day').format('YYYY-MM-DD');
    const nextStartDateString = edge === 'start' ? clampedDate.format('YYYY-MM-DD') : currentStartDateString;
    const nextEndDateString = edge === 'end' ? clampedDate.format('YYYY-MM-DD') : currentEndDateString;

    if (nextStartDateString === currentStartDateString && nextEndDateString === currentEndDateString) {
      return false;
    }

    // Liegt der neue Bereich über/in Ferien → Modal (aufteilen/ignorieren) statt direkt
    // zu speichern. `endDateString` markiert den Resize-Fall für ignore/split.
    const holidayUuids = this._getHolidayUuidsInRange(nextStartDateString, nextEndDateString);
    if (holidayUuids.length > 0) {
      runInAction(() => {
        this.timeConflictModal = {
          isOpen: true,
          card,
          start: dayjs(nextStartDateString).startOf('day').toDate(),
          end: dayjs(nextEndDateString).startOf('day').toDate(),
          startDateString: nextStartDateString,
          endDateString: nextEndDateString,
          holidayUuids,
        };
      });
      return true;
    }

    await this.root.updateCard(card, nextStartDateString, nextEndDateString, card.includeHoliday ?? false, isVirtual);
    return true;
  }

  /** Sammelt die Ferien-UUIDs, die im Bereich `[start, end]` (inklusive) liegen. */
  private _getHolidayUuidsInRange(startDateString: string, endDateString: string): string[] {
    const uuids = new Set<string>();
    const end = dayjs(endDateString).startOf('day');
    let cursor = dayjs(startDateString).startOf('day');
    while (!cursor.isAfter(end, 'day')) {
      const holidayUuid = this.root.dates.get(cursor.format('YYYY-MM-DD'))?.holidayUuid;
      if (holidayUuid) uuids.add(holidayUuid);
      cursor = cursor.add(1, 'day');
    }
    return [...uuids];
  }

  /**
   * Passt eine Karte per Doppelklick strikt in die Lücke ein, in der sie liegt.
   *
   * Die Lücke wird über einen **Referenztag** bestimmt (Start des angeklickten
   * Segments via `range`, sonst der Karten-Start) – also den Tag, der garantiert
   * innerhalb der Ziel-Lücke liegt. Begrenzt wird strikt durch die direkt
   * angrenzenden Karten **und** Ferien; `includeHoliday` wird dabei bewusst
   * ignoriert (die Karte überspannt nie eine Ferienzeit).
   */
  public async autoFitCardToGap(
    card: TeachingBlockCardType | PlacedTeachingBlockCardType,
    { canResizeStart = true, canResizeEnd = true, isVirtual = false, range }: CardAutoFitOptions = {},
  ) {
    if (!card.startDate || !card.endDate) return false;
    if (!canResizeStart && !canResizeEnd) return false;
    if (!this._canAdjustCard(card)) return false;

    const today = dayjs().startOf('day');
    const schoolYearStart = dayjs(this.root.schoolYear.start).startOf('day');
    const schoolYearEnd = dayjs(this.root.schoolYear.end).startOf('day');
    const todayInTimeframe = !today.isBefore(schoolYearStart, 'day') && !today.isAfter(schoolYearEnd, 'day');

    const currentStart = dayjs(card.startDate).startOf('day');
    const currentEnd = dayjs(card.endDate).startOf('day');
    const reference = dayjs(range?.startDateString ?? card.startDate).startOf('day');

    let nextStart = currentStart;
    let nextEnd = currentEnd;

    if (canResizeStart) {
      nextStart = schoolYearStart;

      const previousCard = this.root.cardStore.getPreviousCard(card);
      if (previousCard?.endDate) {
        const cardMinDate = dayjs(previousCard.endDate).startOf('day').add(1, 'day');
        if (cardMinDate.isAfter(nextStart, 'day')) nextStart = cardMinDate;
      }

      const previousHoliday = this._getHolidayBeforeReference(reference);
      if (previousHoliday?.end) {
        const holidayMinDate = dayjs(previousHoliday.end).startOf('day').add(1, 'day');
        if (holidayMinDate.isAfter(nextStart, 'day')) nextStart = holidayMinDate;
      }

      if (this.root.currentVersion.active && todayInTimeframe) {
        const todayMinDate = today.add(1, 'day');
        if (todayMinDate.isAfter(nextStart, 'day')) nextStart = todayMinDate;
      }
    }

    if (canResizeEnd) {
      nextEnd = schoolYearEnd;

      const nextCard = this.root.cardStore.getNextCard(card);
      if (nextCard?.startDate) {
        const cardMaxDate = dayjs(nextCard.startDate).startOf('day').subtract(1, 'day');
        if (cardMaxDate.isBefore(nextEnd, 'day')) nextEnd = cardMaxDate;
      }

      const nextHoliday = this._getHolidayAfterReference(reference);
      if (nextHoliday?.start) {
        const holidayMaxDate = dayjs(nextHoliday.start).startOf('day').subtract(1, 'day');
        if (holidayMaxDate.isBefore(nextEnd, 'day')) nextEnd = holidayMaxDate;
      }
    }

    if (nextEnd.isBefore(nextStart, 'day')) return false;

    const currentStartDateString = currentStart.format('YYYY-MM-DD');
    const currentEndDateString = currentEnd.format('YYYY-MM-DD');
    const nextStartDateString = nextStart.format('YYYY-MM-DD');
    const nextEndDateString = nextEnd.format('YYYY-MM-DD');

    if (nextStartDateString === currentStartDateString && nextEndDateString === currentEndDateString) {
      return false;
    }

    await this.root.updateCard(card, nextStartDateString, nextEndDateString, false, isVirtual);
    return true;
  }

  // ─── Modals ───────────────────────────────────────────────────────────────

  public setConflictModal(modal: ConflictModalType) {
    runInAction(() => {
      if (!modal.isOpen) {
        modal.isLoading = false;
        modal.card = undefined;
        modal.conflicts = undefined;
        modal.startDateString = undefined;
      }
      this.conflictModal = { ...modal };
    });
  }

  public setTimeConflictModal(modal: TimeConflictModalType) {
    runInAction(() => {
      if (!modal.isOpen) {
        modal.isLoading = false;
        modal.card = undefined;
        modal.week = undefined;
        modal.holidays = undefined;
        modal.cards = undefined;
        modal.start = undefined;
        modal.end = undefined;
        modal.startDateString = undefined;
        modal.endDateString = undefined;
      }
      this.timeConflictModal = { ...modal };
    });
  }

  public setCardDurationModal(modal: CardDurationModalType) {
    runInAction(() => {
      if (!modal.isOpen) {
        modal.card = undefined;
        modal.previousCard = undefined;
        modal.previousHoliday = undefined;
        modal.nextCard = undefined;
        modal.nextHoliday = undefined;
      } else {
        const card = modal.card;
        if (!card) return;
        modal.previousCard = this.root.cardStore.getPreviousCard(card) ?? undefined;
        modal.previousHoliday = this.root.cardStore.getPreviousHoliday(card) ?? undefined;
        modal.nextHoliday = this.root.cardStore.getNextHoliday(card) ?? undefined;
        modal.nextCard = this.root.cardStore.getNextCard(card) ?? undefined;
      }
      this.cardDurationModal = { ...modal };
    });
  }

  public setCardRoomsModal(modal: CardRoomsModalType) {
    runInAction(() => {
      if (!modal.isOpen) {
        modal.card = undefined;
      }
      this.cardRoomsModal = { ...modal };
    });
  }

  public setHighlightMode(mode: GridHighlightType) {
    runInAction(() => {
      const index = this.highlightMode.findIndex((m) => m === mode);
      if (index === -1) {
        this.highlightMode.push(mode);
      } else {
        this.highlightMode.splice(index, 1);
      }
    });
  }

  // ─── Zeilen ein-/ausblenden ───────────────────────────────────────────────

  public toggleRowsVisibility(rows: RowItem[]) {
    runInAction(() => {
      const allHidden = rows.every((r) => this.hiddenRows.has(r.value));
      rows.forEach((r) => {
        if (allHidden) {
          this.hiddenRows.delete(r.value);
        } else {
          this.hiddenRows.add(r.value);
        }
      });
    });
  }

  public showAllRows() {
    runInAction(() => {
      this.hiddenRows.clear();
    });
  }

  // ─── Zeilenhöhe ───────────────────────────────────────────────────────────

  public setRowHeight(height: number) {
    if (height < 30 || height > 75) return;
    runInAction(() => {
      this.rowHeight = height;
    });
  }

  public setDefaultRowHeight() {
    runInAction(() => {
      this.rowHeight = 32;
    });
  }

  // ─── Board-Layout (Auswahl/Board-Anordnung + Trennlinien) ─────────────────

  public setBoardLayout(layout: BoardLayout) {
    runInAction(() => {
      this.boardLayout = layout;
    });
  }

  public toggleBoardLayout() {
    runInAction(() => {
      this.boardLayout = this.boardLayout === 'stacked' ? 'split' : 'stacked';
    });
  }

  // Höhe des Auswahl-Bereichs im `stacked`-Layout (px), per Trennlinie justiert.
  public setBoardTopHeight(height: number) {
    runInAction(() => {
      this.boardTopHeight = Math.min(500, Math.max(120, height));
    });
  }

  // Breite des Auswahl-Bereichs im `split`-Layout (% der Board-Breite), per Trennlinie justiert.
  public setBoardSplitWidth(width: number) {
    runInAction(() => {
      this.boardSplitWidth = Math.min(60, Math.max(20, width));
    });
  }

  // Picker-Anteil im `stacked`-Layout (Breite % des Auswahl-Bereichs), per Trennlinie justiert.
  public setBoardPickerWidth(width: number) {
    runInAction(() => {
      this.boardPickerWidth = Math.min(80, Math.max(20, width));
    });
  }

  // Picker-Anteil im `split`-Layout (Höhe % des Auswahl-Bereichs), per Trennlinie justiert.
  public setBoardPickerHeight(height: number) {
    runInAction(() => {
      this.boardPickerHeight = Math.min(80, Math.max(20, height));
    });
  }

  public setShowPreviewMap(show: boolean) {
    runInAction(() => {
      this.showPreviewMap = show;
    });
  }

  public toggleShowPreviewMap() {
    runInAction(() => {
      this.showPreviewMap = !this.showPreviewMap;
    });
  }

  public setPreviewMapContext(context: GridContext) {
    runInAction(() => {
      this.previewMapContext = context;
      // Wer bewusst einen Kontext wählt, will ihn behalten – die Automatik würde ihn sonst
      // bei der nächsten Karte in der Hand sofort wieder überschreiben.
      this.previewMapAuto = false;
    });
  }

  public setPreviewMapAuto(auto: boolean) {
    runInAction(() => {
      this.previewMapAuto = auto;
    });
  }

  // Größe der Vorschau-Karte im Auswahl-Bereich (% je Achse), per Trennlinie justiert.
  public setBoardPreviewMapWidth(width: number) {
    runInAction(() => {
      this.boardPreviewMapWidth = Math.min(70, Math.max(15, width));
    });
  }

  public setBoardPreviewMapHeight(height: number) {
    runInAction(() => {
      this.boardPreviewMapHeight = Math.min(70, Math.max(15, height));
    });
  }

  // ─── Vollbild / Suche ─────────────────────────────────────────────────────

  public setIsFullscreen(isFullscreen: boolean) {
    runInAction(() => {
      this.isFullscreen = isFullscreen;
    });
  }

  public setCardSearchString(value: string) {
    runInAction(() => {
      this.cardSearchString = value;
    });
  }

  public setResizingCard(value: boolean) {
    runInAction(() => {
      this.resizingCard = value;
    });
  }
}
